import { DEFAULT_CHART_COLORS, formatData } from '@utilities';

import { addSlideFooter } from '../slide_footer';
import { parseRichTextToPptRuns } from '../helpers.js';
import {
  CHART_LABEL_NEGATIVE_COLOR,
  CHART_LABEL_POSITIVE_COLOR,
  FONT_FACE,
  FOOTER_TEXT,
  KEY_FINDINGS_TEXT,
  LABEL_COLOR,
  LINE_COLOR,
  READ_AS_TEXT,
  SURVEY_TITLE_OPTIONS,
  TEXT_COLOR,
  TITLE_TEXT,
} from '../constants';

/**
 * transforms and formats chart data format to pptxGen format
 * @param {string} chartDataFormat
 * @return {string}
 */
export const transformToPptxGenFormat = (chartDataFormat) => {
  switch (chartDataFormat) {
    case '+0.0':
      return '+0.0';
    case '0,0':
      return '#,##0';
    case '0,0.0':
      return '#,##0.0';
    case '0,0.0%':
      return '#,##0.0%';
    case '0.0':
      return '0.0';
    case '0.0%':
      return '0.0%';
    case '0.00%':
      return '0.00%';
    case '$0,0':
      return '$#,##0';
    case '$0,0.00':
      return '$#,##0.00';
    default:
      return '#,##0';
  }
};

/**
 * Gets chart data
 * @param {Object} chart
 * @returns {Object}
 */
const toFiniteChartValue = (value) => {
  if (Array.isArray(value)) {
    for (let index = value.length - 1; index >= 0; index -= 1) {
      const resolved = toFiniteChartValue(value[index]);
      if (resolved !== null) return resolved;
    }
    return null;
  }

  const resolved = value && typeof value === 'object' ? (value.y ?? value.value) : value;
  if (typeof resolved === 'number') return Number.isFinite(resolved) ? resolved : null;
  if (typeof resolved === 'string' && resolved.trim()) {
    const numericValue = Number(resolved);
    return Number.isFinite(numericValue) ? numericValue : null;
  }
  return null;
};

const getChartData = (chart) => {
  if (!chart || !Array.isArray(chart.series)) return null;

  const normalizedSeries = chart.series.map((series) => ({
    ...series,
    data: (series.data || []).map((data) => {
      // PptxGenJS serializes non-numeric values into a numeric chart cache. Objects become
      // "[object Object]", which makes PowerPoint repair the generated presentation.
      return data === 'NAN_SENTINEL' ? null : toFiniteChartValue(data);
    }),
  }));

  const chartSeriesLength = normalizedSeries.length;
  const seriesLength = normalizedSeries?.[0]?.data?.length || 0;
  const labels = chart.xAxis?.categories?.slice(0, seriesLength) || [];
  const isHorizontalBar = chart?.barDir === 'bar';
  const reverseIfNeeded = (array, isPercentStacked = false) =>
    isHorizontalBar && !isPercentStacked ? [...array].reverse() : array;
  const dataSeries = reverseIfNeeded(
    normalizedSeries,
    chart?.barGrouping === 'percentStacked'
  )?.map((series) => ({
    labels: reverseIfNeeded(labels),
    name: series.name,
    values: reverseIfNeeded(series.data || []),
  }));

  const maxVal = Math.max(...normalizedSeries.flatMap((s) => s.data)) || 0;
  const format = transformToPptxGenFormat(chart.format);

  return {
    chartOptions: {
      barDir: 'col',
      barGapWidthPct: 50,
      barOverlapPct: -10,
      catAxisLabelColor: LABEL_COLOR,
      catAxisLabelFontSize: 7,
      catAxisLabelRotate: -45,
      catAxisLabelWrap: true,
      catAxisLineShow: false,
      chartColors: DEFAULT_CHART_COLORS.slice(0, chartSeriesLength || 1),
      dataLabelFontSize: 6,
      dataLabelFormatCode: format,
      h: '75%',
      legendColor: TEXT_COLOR,
      legendFontSize: 12,
      legendPos: 't',
      showDataLabels: true,
      showLegend: chartSeriesLength > 1,
      showTitle: false,
      showValue: true,
      valAxisLabelColor: LABEL_COLOR,
      valAxisLabelFontSize: 8,
      valAxisLabelFormatCode: format,
      valAxisLineShow: false,
      valAxisMaxVal: maxVal * 1.2,
      valAxisMinVal: 0,
      valGridLine: { color: LINE_COLOR, size: 0.5, style: 'solid' },
      w: '75%',
      x: 1,
      y: 0.8,
    },
    dataSeries,
  };
};

/**
 * Filters the data series to hide small % series' values < 0.05%, applicable to stacked bar charts
 * @param {Array[Object]} dataSeries
 * @returns {Array[Object]}
 */
const filterDataSeries = (dataSeries) => {
  return dataSeries.map((series) => ({
    ...series,
    values: series?.values?.map((value) => {
      const numericValue = typeof value === 'object' && value?.y != null ? value.y : value;

      return typeof numericValue === 'number' && numericValue >= 0.0005 ? numericValue : null;
    }),
  }));
};

/**
 * Creates chart slide
 * @param {Object} chart
 * @param {string} brokenBy
 * @param {Object} pptx
 * @param {string} readAs
 * @param {string} reportUrl
 * @param {Array}  reportDateRange
 * @param {string} reportType
 * @param {Object} shopperAttributes
 * @param {string} slideTitle
 * @returns {Object}
 */
export const createChartSlide = ({
  brokenBy,
  chart,
  pptx,
  readAs = null,
  reportDateRange,
  reportType,
  reportUrl,
  shopperAttributes,
  slideTitle = null,
}) => {
  const chartData = getChartData(chart);
  if (!chartData) return null;

  const slide = pptx.addSlide();
  const title = chart?.series[1]?.name || chart?.series[0]?.name;

  slide.addText(slideTitle || `${title} by ${brokenBy}`, {
    ...TITLE_TEXT,
    fontSize: slideTitle ? 14 : TITLE_TEXT.fontSize,
  });
  if (readAs)
    slide.addText(readAs, {
      ...READ_AS_TEXT,
      w: '20%',
      x: 7.5,
    });

  const isPercentStacked = chart?.barGrouping === 'percentStacked';
  const shouldSuppressSmallStackedValues = ['normal', 'percentStacked', 'stacked'].includes(
    chart?.barGrouping
  );
  const renderedDataSeries = shouldSuppressSmallStackedValues
    ? filterDataSeries(chartData.dataSeries)
    : chartData.dataSeries;

  const adjustedChartOptions = {
    ...chartData.chartOptions,
    barDir: chart?.barDir,
    barGrouping: chart?.barGrouping,
    showLegend: readAs ? true : chart?.series.length > 1,
    valAxisMaxVal: isPercentStacked ? 1.0 : chartData.chartOptions.valAxisMaxVal,
    x: readAs || chart?.barDir === 'bar' ? 0.2 : chartData.chartOptions?.x,
    y: slideTitle && readAs && chart?.barDir === 'bar' ? 0.8 : chartData.chartOptions?.y,
  };

  if (Array.isArray(chart?.comparisonValues)) {
    const rows = chart.comparisonValues.map((item) => [
      {
        options: {
          align: 'center',
          bold: true,
          color:
            item.delta > 0
              ? CHART_LABEL_POSITIVE_COLOR
              : item.delta < 0
                ? CHART_LABEL_NEGATIVE_COLOR
                : TEXT_COLOR,
          fontFace: FONT_FACE,
          fontSize: 8,
          valign: 'middle',
        },
        text:
          item.value == null || (typeof item.value === 'number' && Number.isNaN(item.value))
            ? '-'
            : formatData({ format: '0.0', value: item.value }),
      },
    ]);

    slide.addTable(rows, {
      colW: [0.5],
      h: '61%',
      w: 1,
      x: 7,
      y: 1.31,
    });

    slide.addChart(pptx.charts.BAR, renderedDataSeries, { ...adjustedChartOptions, w: '70%' });

    addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, shopperAttributes, slide });

    return slide;
  }

  slide.addChart(pptx.charts.BAR, renderedDataSeries, adjustedChartOptions);

  addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, shopperAttributes, slide });

  return slide;
};

/** Creates an editable, shape-based tornado chart with one aligned row per category. */
export const createTornadoChartSlide = ({
  chart,
  pptx,
  reportDateRange,
  reportType,
  reportUrl,
  shopperAttributes,
  slideTitle,
}) => {
  if (!chart?.series?.length || !chart?.xAxis?.categories?.length) return null;

  const slide = pptx.addSlide();
  slide.addText(slideTitle || 'Gains and Losses', { ...TITLE_TEXT, fontSize: 14 });

  const categories = chart.xAxis.categories.slice(0, 15);
  const [gains = {}, losses = {}, netChange = {}] = chart.series;
  const maxAbsoluteValue = Math.max(
    1,
    ...[gains, losses].flatMap((series) =>
      (series.data || []).map((value) => Math.abs(Number(value) || 0))
    )
  );
  const axisLimit = maxAbsoluteValue * 1.1;
  const plot = { h: 3.55, w: 7.3, x: 2.25, y: 1.0 };
  const zeroX = plot.x + plot.w / 2;
  const halfWidth = plot.w / 2;
  const rowHeight = plot.h / categories.length;
  const colors = { gains: '00D778', losses: 'D9534F', net: 'FFCE19' };

  [-1, -0.5, 0, 0.5, 1].forEach((ratio) => {
    const x = zeroX + ratio * halfWidth;
    slide.addShape(pptx.shapes.LINE, {
      h: plot.h,
      line: { color: ratio === 0 ? '7F8C8D' : LINE_COLOR, width: ratio === 0 ? 1 : 0.5 },
      w: 0.001,
      x,
      y: plot.y,
    });
    slide.addText(formatData({ format: chart.format, value: ratio * axisLimit }), {
      align: 'center',
      color: LABEL_COLOR,
      fit: 'shrink',
      fontFace: FONT_FACE,
      fontSize: 7,
      h: 0.18,
      margin: 0,
      w: 0.9,
      x: x - 0.45,
      y: plot.y + plot.h + 0.03,
    });
  });

  const addBar = ({ color, rowY, value }) => {
    const numericValue = Number(value) || 0;
    const width = Math.min(halfWidth, (Math.abs(numericValue) / axisLimit) * halfWidth);
    if (width <= 0) return;
    slide.addShape(pptx.shapes.RECTANGLE, {
      fill: { color },
      h: 0.12,
      line: { type: 'none' },
      w: width,
      x: numericValue < 0 ? zeroX - width : zeroX,
      y: rowY + (rowHeight - 0.12) / 2,
    });
  };

  categories.forEach((category, index) => {
    const rowY = plot.y + index * rowHeight;
    slide.addText(String(category), {
      align: 'right',
      color: LABEL_COLOR,
      fit: 'shrink',
      fontFace: FONT_FACE,
      fontSize: 7,
      h: rowHeight,
      margin: 0,
      valign: 'middle',
      w: 1.85,
      x: 0.25,
      y: rowY,
    });
    addBar({ color: colors.gains, rowY, value: gains.data?.[index] });
    addBar({ color: colors.losses, rowY, value: losses.data?.[index] });
    addBar({ color: colors.net, rowY, value: netChange.data?.[index] });
  });

  const legend = [
    { color: colors.gains, label: gains.name || 'Gains' },
    { color: colors.losses, label: losses.name || 'Losses' },
    { color: colors.net, label: netChange.name || 'Net Change' },
  ];
  legend.forEach(({ color, label }, index) => {
    const x = 3.0 + index * 1.75;
    slide.addShape(pptx.shapes.RECTANGLE, {
      fill: { color },
      h: 0.1,
      line: { type: 'none' },
      w: 0.1,
      x,
      y: 4.93,
    });
    slide.addText(label, {
      color: LABEL_COLOR,
      fit: 'shrink',
      fontFace: FONT_FACE,
      fontSize: 7,
      h: 0.16,
      margin: 0,
      w: 1.55,
      x: x + 0.14,
      y: 4.9,
    });
  });

  addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, shopperAttributes, slide });
  return slide;
};

/**
 * Creates a native LINE chart slide. Prototype for the generic export effort (BI-14103):
 * proves the {series, xAxis.categories, format} IR renders as an editable PptxGenJS chart for
 * a non-bar type. Reuses getChartData (the numeric-series normalizer) and swaps the chart type.
 * @param {Object} chart  { series:[{name,data}], xAxis:{categories}, format }
 * @param {Object} pptx
 * @param {string} readAs
 * @param {Array}  reportDateRange
 * @param {string} reportType
 * @param {string} reportUrl
 * @param {Object} shopperAttributes
 * @param {string} slideTitle
 * @returns {Object}
 */
export const createLineChartSlide = ({
  chart,
  pptx,
  readAs = null,
  reportDateRange,
  reportType,
  reportUrl,
  shopperAttributes,
  slideTitle = null,
}) => {
  const chartData = getChartData(chart);
  if (!chartData) return null;

  const slide = pptx.addSlide();
  const title = chart?.series?.[1]?.name || chart?.series?.[0]?.name;

  slide.addText(slideTitle || title || '', {
    ...TITLE_TEXT,
    fontSize: slideTitle ? 14 : TITLE_TEXT.fontSize,
  });
  if (readAs)
    slide.addText(readAs, {
      ...READ_AS_TEXT,
      w: '20%',
      x: 7.5,
    });

  const lineChartOptions = {
    ...chartData.chartOptions,
    catAxisLabelRotate: -45,
    lineDataSymbol: 'circle',
    lineDataSymbolSize: 5,
    lineSize: 2,
    showLegend: readAs ? true : chart?.series?.length > 1,
  };

  slide.addChart(pptx.charts.LINE, chartData.dataSeries, lineChartOptions);

  addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, shopperAttributes, slide });

  return slide;
};

/**
 * Creates a native combo (column + line) chart slide. Prototype for BI-14103 column+spline
 * (Pareto) exports: proves per-series format-lifting and the native combo path renders as an
 * editable chart. Single value axis for now; dual-axis fidelity is a follow-up.
 * @param {Object} chart { series:[colSeries, lineSeries], xAxis:{categories}, format:[fmt0, fmt1] }
 * @param {Object} pptx
 * @param {string} readAs
 * @param {Array}  reportDateRange
 * @param {string} reportType
 * @param {string} reportUrl
 * @param {Object} shopperAttributes
 * @param {string} slideTitle
 * @returns {Object}
 */
export const createComboChartSlide = ({
  chart,
  pptx,
  readAs = null,
  reportDateRange,
  reportType,
  reportUrl,
  shopperAttributes,
  slideTitle = null,
}) => {
  if (!chart || !Array.isArray(chart.series) || chart.series.length < 2) return null;

  const normalize = (series) =>
    (series?.data || []).map((value) => (value === 'NAN_SENTINEL' ? null : value));
  const [colSeries, lineSeries] = chart.series;
  const seriesLength = colSeries?.data?.length || 0;
  const labels = chart.xAxis?.categories?.slice(0, seriesLength) || [];

  const colData = [{ labels, name: colSeries?.name, values: normalize(colSeries) }];
  const lineData = [{ labels, name: lineSeries?.name, values: normalize(lineSeries) }];

  const colFormat = transformToPptxGenFormat(chart.format?.[0]);
  const lineFormat = transformToPptxGenFormat(chart.format?.[1]);

  const slide = pptx.addSlide();
  slide.addText(slideTitle || colSeries?.name || '', {
    ...TITLE_TEXT,
    fontSize: slideTitle ? 14 : TITLE_TEXT.fontSize,
  });
  if (readAs)
    slide.addText(readAs, {
      ...READ_AS_TEXT,
      w: '20%',
      x: 7.5,
    });

  // Dual-axis scaling: columns keep an auto-scaled primary (left) axis; the cumulative line gets a
  // secondary (right) axis pinned to its natural range (0-100% when the values are fractions), so
  // the exported Pareto matches the on-screen chart instead of sharing one axis.
  const numericValues = (values) => values.filter((value) => typeof value === 'number');
  const colMax = Math.max(0, ...numericValues(colData[0].values));
  const lineMax = Math.max(0, ...numericValues(lineData[0].values));

  slide.addChart(
    [
      {
        data: colData,
        options: {
          barDir: 'col',
          chartColors: [DEFAULT_CHART_COLORS[0]],
          dataLabelFormatCode: colFormat,
        },
        type: pptx.charts.BAR,
      },
      {
        data: lineData,
        options: {
          chartColors: [DEFAULT_CHART_COLORS[1]],
          dataLabelFormatCode: lineFormat,
          lineDataSymbol: 'circle',
          lineDataSymbolSize: 5,
          lineSize: 2,
          secondaryCatAxis: true,
          secondaryValAxis: true,
        },
        type: pptx.charts.LINE,
      },
    ],
    {
      catAxes: [
        {
          catAxisLabelColor: LABEL_COLOR,
          catAxisLabelFontSize: 7,
          catAxisLabelRotate: -45,
          catAxisLineShow: false,
        },
        {
          catAxisHidden: true,
        },
      ],
      dataLabelFontSize: 6,
      h: '75%',
      legendColor: TEXT_COLOR,
      legendFontSize: 12,
      legendPos: 't',
      showDataLabels: true,
      showLegend: true,
      showTitle: false,
      showValue: true,
      valAxes: [
        {
          valAxisLabelColor: LABEL_COLOR,
          valAxisLabelFontSize: 8,
          valAxisLabelFormatCode: colFormat,
          valAxisLineShow: false,
          valAxisMaxVal: colMax > 0 ? colMax * 1.2 : undefined,
          valAxisMinVal: 0,
          valGridLine: { color: LINE_COLOR, size: 0.5, style: 'solid' },
        },
        {
          valAxisLabelColor: LABEL_COLOR,
          valAxisLabelFontSize: 8,
          valAxisLabelFormatCode: lineFormat,
          valAxisLineShow: false,
          valAxisMaxVal: lineMax > 0 && lineMax <= 1 ? 1 : undefined,
          valAxisMinVal: 0,
          valGridLine: { style: 'none' },
        },
      ],
      w: '75%',
      x: 1,
      y: 0.8,
    }
  );

  addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, shopperAttributes, slide });

  return slide;
};

/**
 * Creates chart slide for survey dashboard PowerPoint export
 * @param {Object} options
 * @param {Object} options.chart
 * @param {string} options.compareBy
 * @param {Object} options.pptx
 * @param {string} options.questionNumber
 * @param {string} options.questionTitle
 * @param {string} options.reportType
 * @param {string} options.reportUrl
 * @param {string} options.keyFindings
 * @param {Object} options.shopperAttributes
 * @param {Object} options.title
 * @returns {Object}
 */
export const createSurveyChartSlide = ({
  chart,
  compareBy,
  keyFindings,
  pptx,
  questionNumber,
  questionTitle,
  reportDateRange,
  reportType,
  reportUrl,
  shopperAttributes,
  title,
}) => {
  const chartData = getChartData(chart);
  if (!chartData) return null;

  const questionTitleRuns = parseRichTextToPptRuns(questionTitle);
  if (!questionTitleRuns.length) return null;

  const slide = pptx.addSlide();

  const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
  const compareByLabel = compareBy?.label || compareBy?.value || compareBy;

  const titleRuns = [
    ...(questionPrefix ? [{ text: questionPrefix }] : []),
    ...questionTitleRuns,
    ...(compareByLabel ? [{ text: ` Compared by: ${compareByLabel}` }] : []),
  ];

  const titleOptions = SURVEY_TITLE_OPTIONS;
  slide.addText(titleRuns, titleOptions);

  const chartYPosition = titleOptions.h > 1.0 ? 1.4 : 1.1;
  const hasKeyFindings = keyFindings && Array.isArray(keyFindings) && keyFindings.length > 0;

  const chartHeight = hasKeyFindings ? '65%' : titleOptions.h > 1.0 ? '58%' : '62%';
  const chartWidth = hasKeyFindings ? '55%' : '75%';
  const chartXPosition = hasKeyFindings ? 0.5 : 1.0;

  const adjustedChartOptions = {
    ...chartData.chartOptions,
    barDir: 'col',
    h: chartHeight,
    showDataLabels: true,
    showLegend: chartData.dataSeries.length > 1,
    valGridLine: { color: LINE_COLOR, size: 0.5, style: 'solid' },
    w: chartWidth,
    x: chartXPosition,
    y: chartYPosition,
  };

  slide.addChart(pptx.charts.BAR, chartData.dataSeries, adjustedChartOptions);

  if (hasKeyFindings) {
    const keyFindingsText =
      'Key findings:\n' +
      keyFindings
        .map((finding) => {
          let text = (finding.output || '').toString().trim();
          text = text.replace(/^[•·*]\s*/, '');
          return `• ${text}`;
        })
        .filter((text) => text.length > 2)
        .join('\n');

    if (keyFindingsText !== 'Key Findings:\n') {
      slide.addText(keyFindingsText, {
        ...KEY_FINDINGS_TEXT,
        fontSize: 12,
        h: 1.66,
        lineSpacing: 18,
        margin: [0.15, 0.15, 0.15, 0.15],
        valign: 'top',
        w: 3.0,
        wrap: true,
        x: 6.5,
        y: 1.5,
      });
    }
  }

  addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, shopperAttributes, slide });
  slide.addText(
    [{ text: 'Source: ' }, { options: { hyperlink: { url: reportUrl } }, text: title }],
    FOOTER_TEXT
  );

  return slide;
};

/**
 * Returns structured data for scatter charts
 * @param {Object} chart
 * @returns {Object}
 */
const getScatterData = (chart) => {
  if (!chart || !Array.isArray(chart.series)) return null;

  chart.series.forEach((series) => {
    series.data.forEach((data, index) => {
      if (data === 'NAN_SENTINEL') {
        series.data[index] = null;
      }
    });
  });

  const formatY = transformToPptxGenFormat(chart.formatY);
  const allXValues = [];
  const allYValues = [];
  const pointGroups = [];

  chart.series?.forEach((series) => {
    const points = [];
    series.data?.forEach((point) => {
      if (point && point.x !== undefined && point.y !== undefined) {
        allXValues.push(point.x);
        allYValues.push(point.y);
        points.push(point);
      }
    });
    if (points.length) pointGroups.push({ name: series.name, points });
  });

  let pointOffset = 0;
  const dataSeries = [
    { name: chart.nameX, values: allXValues },
    ...pointGroups.map(({ name, points }) => {
      const leading = Array(pointOffset).fill(null);
      pointOffset += points.length;
      const trailing = Array(allXValues.length - pointOffset).fill(null);
      return {
        labels: [[...leading, ...points.map(({ name: label }) => label || ''), ...trailing]],
        name,
        values: [...leading, ...points.map(({ y }) => y), ...trailing],
      };
    }),
  ];

  return {
    chartOptions: {
      catAxisHidden: false,
      catAxisLabelColor: LABEL_COLOR,
      catAxisLabelFontSize: 8,
      catAxisLabelWrap: true,
      catAxisLineShow: true,
      catAxisMaxVal: chart.xMax ?? Math.min(1, Math.max(...allXValues) * 1.1),
      catAxisMinVal: chart.xMin ?? 0,
      catAxisTitle: chart.nameX,
      catGridLine: { color: LINE_COLOR, size: 0.5, style: 'solid' },
      chartColors: DEFAULT_CHART_COLORS.slice(0, dataSeries.length),
      dataLabelFontSize: 6,
      dataLabelPosition: 't',
      h: '70%',
      legendColor: TEXT_COLOR,
      legendFontSize: 10,
      legendPos: 't',
      lineSize: 0,
      showCatAxisTitle: true,
      showLabel: true,
      showLegend: false,
      showTitle: false,
      showValAxisTitle: true,
      valAxisLabelColor: LABEL_COLOR,
      valAxisLabelFontSize: 8,
      valAxisLabelFormatCode: formatY,
      valAxisLineShow: false,
      valAxisMaxVal: chart.yMax,
      valAxisMinVal: chart.yMin ?? Math.max(0, Math.min(...allYValues) * 0.95),
      valAxisTitle: chart.nameY,
      valGridLine: { color: LINE_COLOR, size: 0.5, style: 'solid' },
      w: '75%',
      x: 0.2,
      y: 1.1,
    },
    dataSeries,
  };
};

/**
 * Creates scatter chart slide with footer text and title
 * @param {string} brokenBy
 * @param {Object} chart
 * @param {Object} pptx
 * @param {string} readAs
 * @param {string} reportUrl
 * @param {Array}  reportDateRange
 * @param {string} reportType
 * @param {Object} shopperAttributes
 * @param {string} slideTitle
 * @returns
 */
export const createScatterChartSlide = ({
  chart,
  pptx,
  readAs = null,
  reportDateRange,
  reportType,
  reportUrl,
  shopperAttributes,
  slideTitle = null,
}) => {
  const chartData = getScatterData(chart);
  if (!chartData) return null;

  const slide = pptx.addSlide();

  slide.addText(slideTitle, { ...TITLE_TEXT, fontSize: slideTitle ? 14 : TITLE_TEXT.fontSize });
  if (readAs)
    slide.addText(readAs, {
      ...READ_AS_TEXT,
      w: '20%',
      x: 7.5,
    });

  slide.addChart(pptx.charts.SCATTER, chartData.dataSeries, chartData.chartOptions);

  if (chart.showRegressionLine) {
    slide.addShape(pptx.ShapeType.line, {
      color: 'B3C9CB',
      flipV: true,
      h: 3.23,
      line: { color: 'B3C9CB', width: 1 },
      w: 6.5,
      x: 0.98,
      y: 1.25,
    });
  }

  addSlideFooter({ pptx, reportDateRange, reportType, reportUrl, shopperAttributes, slide });

  return slide;
};
